/* eslint-disable @typescript-eslint/no-explicit-any */
// react-i18next + TS 4.9.5 workaround — see src/i18n/index.ts.
import React, { useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuth } from '../hooks/useAuth';
import { ProfileType } from '../types/enums';
import { AvailableProfileDto } from '../services/api';

const useTranslation = useTranslationUntyped as any;

interface RequireRoleProps {
    children: React.ReactNode;
    allowedRoles: ProfileType[];
}

/**
 * Gate that requires the active profile to be one of {@link RequireRoleProps.allowedRoles}.
 *
 * Auth v2 (AB#1438): if the caller has a profile that would satisfy the gate
 * but it is not currently active, render an inline prompt asking to switch
 * profile. Accepting calls {@link useAuth.switchProfileV2} and then reloads
 * the page so the original URL is served under the new active profile.
 *
 * Legacy (flag off): preserves existing behavior — redirect to `/` on mismatch.
 */
export const RequireRole: React.FC<RequireRoleProps> = ({ children, allowedRoles }) => {
    const { isAuthenticated, userType } = useAuthContext();
    const { isAuthV2Enabled, switchProfileV2, loading } = useAuth();
    const location = useLocation();
    const [switching, setSwitching] = useState(false);

    // Pull availableProfiles from JWT so we can tell whether the caller owns a
    // profile that matches the required role set.
    const { profiles } = useMemo(readAvailableProfilesFromToken, []);

    if (!isAuthenticated) {
        return <Navigate to="/form/login" state={{ from: location }} replace />;
    }

    if (userType === null || userType === undefined) {
        return <Navigate to="/form/login" replace />;
    }

    if (allowedRoles.includes(userType as ProfileType)) {
        return <>{children}</>;
    }

    // Active profile doesn't match. Auth v2: if the user owns a matching
    // profile, offer to switch. Otherwise fall through to the legacy redirect.
    if (isAuthV2Enabled) {
        const matching = profiles.find(p => allowedRoles.includes(p.type) && p.type !== ProfileType.Customer);
        if (matching) {
            return (
                <DeepLinkSwitchPrompt
                    target={matching}
                    loading={switching || loading}
                    onConfirm={async () => {
                        setSwitching(true);
                        const result = await switchProfileV2(matching.id);
                        if (result && 'needsElevation' in result) {
                            // Elevation required — fall back to home so the global
                            // switcher UX (ElevationModal in the account menu) handles it.
                            setSwitching(false);
                            window.location.replace('/');
                            return;
                        }
                        // On success, useAuth navigates home via the state-reset
                        // utility. Reload on the current URL so children re-render
                        // under the new active profile's claims.
                        setTimeout(() => { window.location.reload(); }, 50);
                    }}
                    onCancel={() => { window.location.replace('/'); }}
                />
            );
        }
    }

    return <Navigate to="/" replace />;
};

const DeepLinkSwitchPrompt: React.FC<{
    target: AvailableProfileDto;
    loading: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}> = ({ target, loading, onConfirm, onCancel }) => {
    const { t } = useTranslation('auth');

    const roleLabel = (() => {
        switch (target.type) {
            case ProfileType.SalonOwner: return t('roles.salonOwner');
            case ProfileType.SalonStaff: return t('roles.salonStaff');
            case ProfileType.SuperAdmin: return t('roles.superAdmin');
            case ProfileType.Customer: return t('roles.customer');
            default: return String(target.type);
        }
    })();

    return (
        <div style={{
            maxWidth: 460,
            margin: '10vh auto',
            padding: '24px',
            background: 'var(--bg-surface)',
            borderRadius: 12,
            border: '1px solid var(--border-subtle)',
            boxShadow: '0 10px 25px -12px rgba(97,56,224,.25)',
            fontFamily: 'inherit',
        }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>{t('deepLink.title')}</h2>
            <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {t('deepLink.body', { role: roleLabel, business: target.businessName ?? '' })}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={loading}
                    style={{
                        padding: '10px 14px',
                        borderRadius: 8,
                        border: '1px solid var(--border-subtle)',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontWeight: 600,
                    }}
                >
                    {t('deepLink.cancel')}
                </button>
                <button
                    type="button"
                    onClick={onConfirm}
                    disabled={loading}
                    style={{
                        padding: '10px 14px',
                        borderRadius: 8,
                        border: 0,
                        background: 'var(--primary)',
                        color: 'var(--on-primary)',
                        cursor: 'pointer',
                        fontWeight: 600,
                    }}
                >
                    {t('deepLink.confirm')}
                </button>
            </div>
        </div>
    );
};

function readAvailableProfilesFromToken(): { profiles: AvailableProfileDto[] } {
    try {
        const token = localStorage.getItem('accessToken');
        if (!token) return { profiles: [] };
        const payload = token.split('.')[1];
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        const raw = decoded['availableProfiles'];
        if (!raw) return { profiles: [] };
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!Array.isArray(parsed)) return { profiles: [] };

        const profiles: AvailableProfileDto[] = parsed.map((p: any) => ({
            id: typeof p.id === 'number' ? p.id : parseInt(p.id, 10),
            type: mapProfileType(p.type),
            businessId: p.businessId ?? null,
            businessName: p.businessName ?? null,
        }));
        return { profiles };
    } catch {
        return { profiles: [] };
    }
}

function mapProfileType(raw: unknown): ProfileType {
    if (typeof raw === 'number') return raw as ProfileType;
    if (typeof raw === 'string') {
        switch (raw) {
            case 'Customer': return ProfileType.Customer;
            case 'SalonOwner': return ProfileType.SalonOwner;
            case 'SalonStaff': return ProfileType.SalonStaff;
            case 'SuperAdmin': return ProfileType.SuperAdmin;
            default: return ProfileType.Customer;
        }
    }
    return ProfileType.Customer;
}
